import { useCallback, useState } from 'react';

import { type TradingAccount, TradingAccountSchema } from '../../../api';
import { useApiClient } from '../../../lib/api/api-context';
import { type ApiQueryResult, useApiQuery } from '../../../lib/api/use-api-query';
import { useAppVisible } from '../../../lib/telegram/use-app-visible';

/** Milliseconds between two reads while a verification runs (S6 R5, Proposal). */
const VERIFY_POLL_INTERVAL_MS = 1000;

/** What {@link useTradingAccount} gives the route. */
export type TradingAccountRead = ApiQueryResult<TradingAccount> & {
  /**
   * Says a verification is running (the 202 of `POST /trading-account`): polling starts at once,
   * without waiting for a read to say `VERIFYING`.
   */
  readonly watch: () => void;
};

/**
 * `GET /trading-account` for E4 (S6 R5, R30): once on open, then every second while the status is
 * `VERIFYING` and the app is visible, and never otherwise (AC33; the 5 s rule of M7 is for Home
 * and Positions). The poll is only switched on while a verification runs, so a read that says
 * `VERIFIED`, `REJECTED` or `UNREACHABLE` ends it, and nothing polls on `NONE`.
 */
export function useTradingAccount(): TradingAccountRead {
  const api = useApiClient();
  const visible = useAppVisible();
  const load = useCallback(
    (signal: AbortSignal) => api.get('/trading-account', TradingAccountSchema, signal),
    [api],
  );
  const [watching, setWatching] = useState(false);
  // Poll only while a verification runs. Starting without `poll` also lets the first read leave at
  // once: `useApiQuery` defers it only for a polled query mounted hidden.
  const query = useApiQuery(load, {
    poll: watching ? { intervalMs: VERIFY_POLL_INTERVAL_MS, active: visible } : undefined,
  });
  // Every answer says whether to keep polling (adjusted during render, as `useApiQuery` does, so
  // no effect lags behind the answer).
  const [answerSeen, setAnswerSeen] = useState(query.data);
  if (answerSeen !== query.data) {
    setAnswerSeen(query.data);
    if (query.data !== undefined) setWatching(query.data.status === 'VERIFYING');
  }
  const watch = useCallback(() => {
    setWatching(true);
  }, []);
  return { ...query, watch };
}
